
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, Users, TrendingUp, Database, Eye, Calendar, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StatCard from '../StatCard';
import { useRoleDashboardStats } from '@/hooks/use-role-dashboard-stats';

const BenchmarkManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { totalEntries, todayEntries, marketsTracked, recentEntries, isLoading } = useRoleDashboardStats('benchmark_manager');

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInDays === 1) return '1 day ago';
    return `${diffInDays} days ago`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Benchmark Manager Dashboard</h1>
          <p className="text-muted-foreground">
            Monitor benchmark performance and oversee price data collection activities
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/reports')} variant="default" className="gap-2">
            <FileText className="h-4 w-4" />
            Performance Report
          </Button>
          <Button onClick={() => navigate('/price-benchmarking/overview')} variant="outline" className="gap-2">
            <Eye className="h-4 w-4" />
            View Analytics
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Price Entries"
          value={isLoading ? '--' : totalEntries?.toString() || '0'}
          icon={<Database className="h-4 w-4" />}
        />
        <StatCard
          title="Today's Entries"
          value={isLoading ? '--' : todayEntries?.toString() || '0'}
          icon={<Calendar className="h-4 w-4" />}
        />
        <StatCard
          title="Active Markets"
          value={isLoading ? '--' : marketsTracked?.toString() || '0'}
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <StatCard
          title="Data Sources"
          value="6"
          icon={<BarChart3 className="h-4 w-4" />}
        />
      </div>

      {/* Performance Overview */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent Data Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Data Activity</CardTitle>
            <CardDescription>Latest price data entries across all markets</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="flex items-center justify-between py-2 border-b">
                      <div className="space-y-1">
                        <div className="h-4 bg-muted rounded w-48"></div>
                        <div className="h-3 bg-muted rounded w-32"></div>
                      </div>
                      <div className="h-3 bg-muted rounded w-16"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentEntries && recentEntries.length > 0 ? (
              <div className="space-y-3">
                {recentEntries.slice(0, 5).map((entry: any, index: number) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0">
                    <div>
                      <p className="font-medium">{entry.product_name} - {entry.source_type}</p>
                      <p className="text-sm text-muted-foreground">
                        {entry.price} ETB/kg - {entry.location}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatTimeAgo(entry.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No recent activity</p>
                <p className="text-sm">Price data entries will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Management Tools</CardTitle>
            <CardDescription>Quick access to benchmark management features</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={() => navigate('/reports')} 
              variant="default" 
              className="w-full justify-start gap-2"
            >
              <FileText className="h-4 w-4" />
              Performance Report
            </Button>
            <Button 
              onClick={() => navigate('/price-benchmarking/overview')} 
              variant="outline" 
              className="w-full justify-start gap-2"
            >
              <BarChart3 className="h-4 w-4" />
              View Price Analytics
            </Button>
            <Button 
              onClick={() => navigate('/price-benchmarking/benchmark')} 
              variant="outline" 
              className="w-full justify-start gap-2"
            >
              <TrendingUp className="h-4 w-4" />
              Benchmark Comparison
            </Button>
            <Button 
              onClick={() => navigate('/users')} 
              variant="outline" 
              className="w-full justify-start gap-2"
            >
              <Users className="h-4 w-4" />
              Manage Benchmark Users
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BenchmarkManagerDashboard;
